import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { CART_ORIGIN } from "@/constants";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import { useCreateCareplanCart } from "@/domains/careplan/mutations/use-create-careplan-cart";
import { useUpdateCareplanCartDiscount } from "@/domains/careplan/mutations/use-update-careplan-cart-discount";
import { useCartSellingPlans } from "@/domains/careplan/queries/use-cart-selling-plans";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { env } from "@/env";

export const useCareplanCart = () => {
  const { data: cart, ...queryResult } = useQuery({
    ...careplanQueryOptions.cart,
  });
  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const { discount } = useSearch({ from: "/_private" });
  const { cartProducts, cartVariantIds, id: careplanId, version } = useCareplanContext();
  const { data: availableSellingPlans } = useCartSellingPlans(cartVariantIds);
  const { mutate: createCart } = useCreateCareplanCart();
  const { mutate: updateCartDiscount } = useUpdateCareplanCartDiscount();

  useEffect(() => {
    const cartNonExistentOrExpired = cart === null;
    const localStorageVersion = localStorage.getItem("careplanVersion");
    const hasCareplanChanged = !!localStorageVersion && localStorageVersion !== String(version);
    const shouldCreateCart =
      (cartNonExistentOrExpired || hasCareplanChanged) &&
      availableSellingPlans &&
      !isProfileLoading;
    const shouldUpdateDiscount = cart && discount;
    const shouldStoreVersion = hasCareplanChanged || !localStorageVersion;

    if (shouldCreateCart) {
      if (shouldStoreVersion) {
        localStorage.setItem("careplanVersion", String(version));
      }

      createCart({
        input: {
          buyerIdentity: profile?.email
            ? {
                email: profile.email,
              }
            : undefined,
          lines: cartProducts.map((product) => {
            const quantity = "quantity" in product ? product.quantity : 1;

            return {
              quantity,
              merchandiseId: `gid://shopify/ProductVariant/${product.variantId}`,
              sellingPlanId: availableSellingPlans.monthly?.id,
            };
          }),
          attributes: [
            { key: "origin", value: CART_ORIGIN.CAREPLAN },
            { key: "careplanId", value: String(careplanId) },
          ],
          discountCodes: [discount ?? env.VITE_CAREPLAN_DISCOUNT_CODE],
        },
      });
    } else if (shouldUpdateDiscount) {
      updateCartDiscount({
        cartId: cart.id,
        discountCodes: [discount],
      });
    }
  }, [
    availableSellingPlans,
    careplanId,
    cart,
    cartProducts,
    discount,
    isProfileLoading,
    profile,
    version,
    createCart,
    updateCartDiscount,
  ]);

  return {
    cart,
    ...queryResult,
  };
};
